import { ReactNode } from 'react'
import { ButtonLink } from '@components/ui/Button'
import Link from '@components/ui/Link'
import { ReactComponent as TwitterIcon } from '@svgs/social/twitter.svg'
import { ReactComponent as InstagramIcon } from '@svgs/social/instagram.svg'
import { ReactComponent as FacebookIcon } from '@svgs/social/facebook.svg'
import { ReactComponent as PinterestIcon } from '@svgs/social/pinterest.svg'
import { ReactComponent as EmailIcon } from '@svgs/keyline/email.svg'
import { ReactComponent as PhoneIcon } from '@svgs/keyline/phone.svg'
import { ReactComponent as WhatsappIcon } from '@svgs/keyline/whatsapp.svg'
import Translations from '@helpers/translations'
import {
  getHelpdeskLink,
  getHelpdeskOpenTimes,
  getPhoneNumber,
  getSMSNumber,
  getWhatsAppDisplayNumber,
  getWhatsAppPhoneNumber
} from '@helpers/env'
import styles from './styles.module.scss'
import PageHeader from '@components/pages/components/PageHeader'

const socialIcons = {
  twitter: TwitterIcon,
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  pinterest: PinterestIcon
} as const

type ContactSectionProps = {
  title: string
  byline: string
  rightSideElement: ReactNode
}
function ContactSection({
  title,
  byline,
  rightSideElement
}: ContactSectionProps) {
  return (
    <section className={styles.contact__section}>
      <div className={styles.contact__content}>
        <h3 className={styles.contact__title}>{title}</h3>
        <p className={styles.contact__byline}>{byline}</p>
      </div>
      <div className={styles.contact__content}>{rightSideElement}</div>
    </section>
  )
}

type ContactSocialProps = {
  name: keyof typeof socialIcons
  href: string
}

function ContactSocial({ name, href }: ContactSocialProps) {
  const IconSocial = socialIcons[name]
  return (
    <li className={styles.social__item}>
      <Link classes={styles.social__link} underlined={false} href={href}>
        <IconSocial />
      </Link>
    </li>
  )
}

function ContactUs() {
  return (
    <>
      <PageHeader title={Translations.localise('pages.contact.title')} />
      <div className={`${styles.contact}`}>
        <ContactSection
          title={Translations.localise('pages.contact.have-a-question.title')}
          byline={Translations.localise('pages.contact.have-a-question.byline')}
          rightSideElement={
            <ButtonLink
              variant="primary"
              classes={styles.contact__button}
              rel="noopener noreferrer"
              target="_blank"
              href={getHelpdeskLink()}
            >
              {Translations.localise('pages.contact.check-faqs')}
            </ButtonLink>
          }
        />

        <ContactSection
          title={Translations.localise('pages.contact.send-us-an-email.title')}
          byline={Translations.localise(
            'pages.contact.send-us-an-email.byline'
          )}
          rightSideElement={
            <div className={styles.contact__rightside}>
              <EmailIcon className={styles.icon_contactEmail} />
              <Link
                href="mailto: hello@spoke-london.com"
                text="hello@spoke-london.com"
              />
            </div>
          }
        />

        <ContactSection
          title={Translations.localise('pages.contact.whatsapp-us.title')}
          byline={Translations.localise('pages.contact.whatsapp-us.byline')}
          rightSideElement={
            <div className={styles.contact__rightside}>
              <WhatsappIcon
                width="38"
                height="27"
                className={styles.icon_contactWhatsapp}
              />
              <Link
                href={`https://wa.me/${getWhatsAppPhoneNumber()}`}
                text={`${getWhatsAppDisplayNumber()}`}
              />
            </div>
          }
        />

        <ContactSection
          title={Translations.localise('pages.contact.need-to-talk.title')}
          byline={`${Translations.localise(
            'pages.contact.need-to-talk.byline'
          )} (${getHelpdeskOpenTimes()}).`}
          rightSideElement={
            <div className={styles.contact__rightside}>
              <PhoneIcon className={styles.icon_contactPhone} />
              <Link
                href={`tel: ${getPhoneNumber()}`}
                text={`${getSMSNumber()}`}
              />
            </div>
          }
        />

        <ContactSection
          title={Translations.localise('pages.contact.press-enquiries.title')}
          byline={Translations.localise('pages.contact.press-enquiries.byline')}
          rightSideElement={
            <div className={styles.contact__rightside}>
              <EmailIcon className={styles.icon_contactEmail} />
              <Link
                href="mailto: press@spoke-london.com"
                text="press@spoke-london.com"
              />
            </div>
          }
        />

        <ContactSection
          title={Translations.localise('pages.contact.socialise.title')}
          byline={Translations.localise('pages.contact.socialise.byline')}
          rightSideElement={
            <ul className={styles.social}>
              <ContactSocial
                name="instagram"
                href="https://www.instagram.com/spoke_london"
              />
              <ContactSocial
                name="facebook"
                href="https://www.facebook.com/spokefit"
              />
            </ul>
          }
        />

        <ContactSection
          title={Translations.localise('pages.contact.directions.title')}
          byline={Translations.localise('pages.contact.directions.byline')}
          rightSideElement={
            <>
              <p className={styles.contact__address}>
                1st and 2nd Floor, 26 George Street, Richmond, TW9 1HY
              </p>
              <div
                className={styles.contact__location}
                data-toggle-attr="data-open"
              >
                <p>
                  {Translations.localise(
                    'pages.contact.directions.get-directions'
                  )}
                  :
                </p>

                <ul>
                  <li>
                    <Link
                      href="https://www.google.com/maps/place/SPOKE/@51.4607011,-0.304464,19z/data=!4m5!3m4!1s0x487604ebaaaaaaab:0xa8a245d9f7e82d95!8m2!3d51.4607011!4d-0.304464"
                      text="Google maps"
                    />
                  </li>
                  <li>
                    <Link
                      href="https://maps.apple.com/place?address=25-26+George+Street%2C+Richmond%2C+TW9+1HY%2C+England&coordinate=51.460755%2C-0.304516&name=25-26+George+Street"
                      text="Apple maps"
                    />
                  </li>
                  <li>
                    <Link
                      href="https://citymapper.com/go/554e5vm1ca"
                      text="Citymapper"
                    />
                  </li>
                </ul>
              </div>
            </>
          }
        />
      </div>
    </>
  )
}

export default ContactUs
